import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCategories } from '../lib/api';
import { categoryInfo } from '../data/categoryFaqs';
import JsonLd from '../components/JsonLd';
import type { Category } from '../types';

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute('name', name); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

const BASE = 'https://marinelink.co.nz';

export default function FaqHub() {
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    document.title = 'Marine Industry FAQs - New Zealand | MarineLink';
    setMeta('description', 'Answers to the most common New Zealand marine industry questions: boat ramps, marinas, charters, fishing rules, insurance, training and more.');
    fetchCategories().then(setCategories);
    return () => { document.title = 'MarineLink - NZ Marine Industry Directory'; };
  }, []);

  // Only categories that have FAQ content, preserving category order
  const sections = categories
    .filter((c) => categoryInfo[c.slug])
    .map((c) => ({ category: c, info: categoryInfo[c.slug] }));

  const totalFaqs = sections.reduce((n, s) => n + s.info.faqs.length, 0);

  const faqGraph = sections.map((s) => ({
    '@type': 'FAQPage',
    '@id': `${BASE}/faq#${s.category.slug}`,
    name: `${s.category.name} FAQs`,
    mainEntity: s.info.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }));

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'FAQs', item: `${BASE}/faq` },
    ],
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-8 py-8 md:py-12">
      <JsonLd id="faq-hub" data={{ '@context': 'https://schema.org', '@graph': [breadcrumbSchema, ...faqGraph] }} />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted-light dark:text-text-muted mb-6 flex-wrap">
        <Link to="/" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <span className="text-text-primary-light dark:text-text-primary">FAQs</span>
      </div>

      {/* Hero */}
      <div className="mb-10 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-text-primary-light dark:text-text-primary mb-4">
          New Zealand marine industry FAQs
        </h1>
        <p className="text-text-secondary-light dark:text-text-secondary text-lg leading-relaxed">
          {totalFaqs} expert answers covering boat ramps, marinas, charters, fishing rules, insurance,
          maintenance, training and more, written for New Zealand boaties.
        </p>
      </div>

      {/* Jump links */}
      <nav className="mb-12 border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface p-5" aria-label="FAQ categories">
        <h2 className="font-display font-bold text-text-primary-light dark:text-text-primary mb-3 text-sm uppercase tracking-wide">Jump to a topic</h2>
        <div className="flex flex-wrap gap-2">
          {sections.map((s) => (
            <a
              key={s.category.slug}
              href={`#${s.category.slug}`}
              className="px-3 py-1.5 rounded-full border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] text-sm text-text-secondary-light dark:text-text-secondary hover:border-accent hover:text-accent transition-colors"
            >
              {s.category.name}
            </a>
          ))}
        </div>
      </nav>

      {/* Sections */}
      <div className="space-y-14">
        {sections.map((s) => (
          <section key={s.category.slug} id={s.category.slug} aria-labelledby={`h-${s.category.slug}`} className="scroll-mt-24">
            <div className="flex items-baseline justify-between gap-4 mb-5 flex-wrap">
              <h2 id={`h-${s.category.slug}`} className="font-display text-2xl font-bold text-text-primary-light dark:text-text-primary">
                {s.category.name}
              </h2>
              <Link to={`/category/${s.category.slug}`} className="text-accent text-sm hover:underline shrink-0">
                Browse {s.category.name.toLowerCase()} listings →
              </Link>
            </div>
            <div className="space-y-3">
              {s.info.faqs.map((f) => (
                <details key={f.q} className="group border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface overflow-hidden">
                  <summary className="cursor-pointer list-none px-5 py-4 font-medium text-text-primary-light dark:text-text-primary flex items-center justify-between gap-4 hover:text-accent transition-colors">
                    <span>{f.q}</span>
                    <span className="text-text-muted-light dark:text-text-muted group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] pt-4">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
